import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import ReuploadApiResponseDialog from '../../../components/ReuploadDocuments/ReuploadApiResponseDialog';
import ReuploadDocumentsPageLayout from '../../../components/ReuploadDocuments/ReuploadDocumentsPageLayout';
import {
    PP_REUPLOAD_STEP_PAGE_NAME,
    ReuploadDocumentSlot,
    ReuploadDocumentsGrid,
    buildReuploadDocumentRemarks,
    buildReuploadStatusBarFromSlots,
    createReuploadUploadHandler,
    isDocFlagApproved,
    isDocFlagRejected,
    isReuploadSubmitDisabled,
} from '../../../components/ReuploadDocuments/reuploadDocumentUi';
import { cfpBaseUrl } from '../../../network/urls';
import { portalName } from '../../../utils/PortalName';

const AdvancePaymentUpdateDocuments = () => {
    const initialValues = {
        iv_applicant_cnic_document_front: null,
        iv_applicant_cnic_document_back: null,
    };

    const [apiResponseDialog, setApiResponseDialog] = useState({
        open: false,
        type: 'success',
        message: '',
        documentRemarks: [],
    });
    const [submitCompleted, setSubmitCompleted] = useState(false);
    const [documents, setDocuments] = useState({});
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [applicantCnicDocumentFrontThumbnail, setApplicantCnicDocumentFrontThumbnail] = useState('');
    const [applicantCnicDocumentBackThumbnail, setApplicantCnicDocumentBackThumbnail] = useState('');
    const [applicantCnicFrontError, setApplicantCnicFrontError] = useState('');
    const [applicantCnicBackError, setApplicantCnicBackError] = useState('');
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const [error, setError] = useState(false);
    const [cnicFrontReUploaded, setCnicFrontReUploaded] = useState(false);
    const [cnicBackReUploaded, setCnicBackReUploaded] = useState(false);

    const { state } = useLocation();
    const { dialogRowData } = state || {};
    const { token } = useSelector((authState) => authState.auth);
    const navigate = useNavigate();

    const isCnicFrontRejected = isDocFlagRejected(documents?.applicantCNICFrontFlag);
    const isCnicBackRejected = isDocFlagRejected(documents?.applicantCNICBackFlag);

    const reuploadSlots = useMemo(() => ([
        { isRejected: isCnicFrontRejected, isReUploaded: cnicFrontReUploaded },
        { isRejected: isCnicBackRejected, isReUploaded: cnicBackReUploaded },
    ]), [isCnicFrontRejected, isCnicBackRejected, cnicFrontReUploaded, cnicBackReUploaded]);

    const ppReuploadStatusBar = useMemo(
        () => buildReuploadStatusBarFromSlots(reuploadSlots),
        [reuploadSlots],
    );

    const getAdvancePaymentDocuments = async (appId) => {
        setDocumentsLoading(true);
        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        myHeaders.append('Content-Type', 'application/json');

        try {
            const response = await fetch(`${cfpBaseUrl}advancePayment/getAdvancePaymentDocuments`, {
                method: 'POST',
                headers: myHeaders,
                body: JSON.stringify({ applicationId: appId }),
                redirect: 'follow',
            });
            const result = await response.json();

            if (result.status === '200' && result.data?.length > 0) {
                const docData = result.data[0];
                setDocuments(docData);

                if (docData.applicantCNICFront) {
                    setApplicantCnicDocumentFrontThumbnail(docData.applicantCNICFront);
                }
                if (docData.applicantCNICBack) {
                    setApplicantCnicDocumentBackThumbnail(docData.applicantCNICBack);
                }
            } else {
                setDocuments({});
            }
        } catch (fetchError) {
            setDocuments({});
        } finally {
            setDocumentsLoading(false);
        }
    };

    useEffect(() => {
        if (dialogRowData?.applicationId) {
            getAdvancePaymentDocuments(dialogRowData.applicationId);
        } else {
            setDocumentsLoading(false);
        }
    }, [dialogRowData?.applicationId]);

    const { handleChange, handleSubmit, setFieldValue } = useFormik({
        initialValues,
        validationSchema: Yup.object({
            iv_applicant_cnic_document_front: Yup.mixed().required(
                'Please upload CNIC front in jpg, jpeg, png or pdf format.',
            ),
            iv_applicant_cnic_document_back: Yup.mixed().required(
                'Please upload CNIC back in jpg, jpeg, png or pdf format.',
            ),
        }),
        onSubmit: async () => {
            setViewBackdrop(true);

            const myHeaders = new Headers();
            myHeaders.append('Content-Type', 'application/json');
            myHeaders.append('Authorization', token);

            const raw = JSON.stringify({
                applicationId: dialogRowData.applicationId,
                submittedThrough: portalName,
                applicationProgressId: 1,
                objectionStatus: 0,
                remarks: 'Reuploaded',
                remarksDetail: '',
                applicantCNICBack: applicantCnicDocumentBackThumbnail,
                applicantCNICFront: applicantCnicDocumentFrontThumbnail,
                applicantCNICFrontFlag: 2,
                applicantCNICBackFlag: 2,
            });

            try {
                const response = await fetch(`${cfpBaseUrl}advancePayment/reuploadAdvancePaymentDocuments`, {
                    method: 'POST',
                    headers: myHeaders,
                    body: raw,
                    redirect: 'follow',
                });
                const result = await response.json();

                if (result.status === '200' || result.status === 200) {
                    setSubmitCompleted(true);
                    setApiResponseDialog({
                        open: true,
                        type: 'success',
                        message: result.message || 'Documents re-uploaded successfully.',
                        documentRemarks: buildReuploadDocumentRemarks(documents, dialogRowData),
                    });
                } else {
                    setApiResponseDialog({
                        open: true,
                        type: 'error',
                        message: result.message || 'Something went wrong.',
                        documentRemarks: [],
                    });
                }
            } catch (submitError) {
                setApiResponseDialog({
                    open: true,
                    type: 'error',
                    message: 'Failed to re-upload documents.',
                    documentRemarks: [],
                });
            } finally {
                setViewBackdrop(false);
            }
        },
    });

    const handleCnicFrontUpload = createReuploadUploadHandler({
        handleChange,
        setUploadError: setApplicantCnicFrontError,
        setThumbnail: (thumbnail) => {
            setApplicantCnicDocumentFrontThumbnail(thumbnail);
            setFieldValue('iv_applicant_cnic_document_front', thumbnail);
        },
        setReUploaded: setCnicFrontReUploaded,
        filePrefix: 'copyOfApplicantCNICFront',
    });

    const handleCnicBackUpload = createReuploadUploadHandler({
        handleChange,
        setUploadError: setApplicantCnicBackError,
        setThumbnail: (thumbnail) => {
            setApplicantCnicDocumentBackThumbnail(thumbnail);
            setFieldValue('iv_applicant_cnic_document_back', thumbnail);
        },
        setReUploaded: setCnicBackReUploaded,
        filePrefix: 'copyOfApplicantCNICBack',
    });

    const isCnicFrontApproved = isDocFlagApproved(documents?.applicantCNICFrontFlag);
    const isCnicBackApproved = isDocFlagApproved(documents?.applicantCNICBackFlag);
    const disableSubmit = isReuploadSubmitDisabled(reuploadSlots, submitCompleted);

    const handleApiResponseDialogClose = () => {
        const wasSuccess = apiResponseDialog.type === 'success';
        setApiResponseDialog((prev) => ({ ...prev, open: false }));
        if (wasSuccess) {
            navigate('/advance-payment-table', { state: { refreshAdvancePaymentList: true } });
        }
    };

    return (
        <>
            <ReuploadDocumentsPageLayout
                pageTitle="Advance Payment - Re-Upload Documents"
                statusBar={ppReuploadStatusBar}
                stepPageName={PP_REUPLOAD_STEP_PAGE_NAME}
                dialogRowData={dialogRowData}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/advance-payment-table')}
                submitDisabled={disableSubmit}
                documentsLoading={documentsLoading}
                viewBackdrop={viewBackdrop}
                error={error}
                onErrorClose={() => setError('')}
            >
                <ReuploadDocumentsGrid>
                    <ReuploadDocumentSlot
                                    label="CNIC Front"
                                    thumbnail={applicantCnicDocumentFrontThumbnail}
                                    isApproved={isCnicFrontApproved}
                                    isRejected={isCnicFrontRejected}
                                    isReUploaded={cnicFrontReUploaded}
                                    inputId="iv_applicant_cnic_document_front"
                                    inputName="iv_applicant_cnic_document_front"
                                    onUpload={handleCnicFrontUpload}
                                    uploadError={applicantCnicFrontError}
                                />
                                <ReuploadDocumentSlot
                                    label="CNIC Back"
                                    thumbnail={applicantCnicDocumentBackThumbnail}
                                    isApproved={isCnicBackApproved}
                                    isRejected={isCnicBackRejected}
                                    isReUploaded={cnicBackReUploaded}
                                    inputId="iv_applicant_cnic_document_back"
                                    inputName="iv_applicant_cnic_document_back"
                                    onUpload={handleCnicBackUpload}
                                    uploadError={applicantCnicBackError}
                                />
                </ReuploadDocumentsGrid>
            </ReuploadDocumentsPageLayout>

            <ReuploadApiResponseDialog
                open={apiResponseDialog.open}
                type={apiResponseDialog.type}
                onClose={handleApiResponseDialogClose}
                message={apiResponseDialog.message}
                applicationId={dialogRowData?.applicationId}
                documentRemarks={apiResponseDialog.documentRemarks}
                errorTitle="Request Failed"
            />
        </>
    );
};

export default AdvancePaymentUpdateDocuments;
